/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import {
    NSInput, NSLabel, NSDatePicker,
} from 'bricks';
import NSInputNumberStepper from 'bricks/NSInputNumberStepper/NSInputNumberStepper';
import {
    parkingOptions, industrialParkingOptions, STRUCTURED_PARKING, SURFACE_PARKING, UNDERGROUND_PARKING, TRAILER_PARKING, HOLD_PERIOD_TOOLTIP_TEXT,
} from 'views/ProFormaTable/constants';
import RenderIf from 'components/RenderIf/RenderIf';
import CalculateSizeCard from 'views/ProFormaTable/components/ProForma/ProFormaGeneralInformation/CalculateSizeCard/CalculateSizeCard';
import { ProFormaModelIds } from 'constants/proForma';
import ProFormaSizeOptionalFields from 'views/ProFormaTable/components/ProForma/ProFormaGeneralInformation/ProFormaSizeDetails/ProFormaSizeOptionalFields/ProFormaSizeOptionalFields';
import './BasicInformation.scss';
import { useGrossBuildableArea } from 'views/ProFormaWizard/BasicInformation/hooks/useGrossBuildableArea';
import { numeralFormatter } from 'ns_libs/formatter';
import { IOptionAnyValue } from 'bricks/types';
import ParkingSelector from 'views/ProFormaTable/components/ProForma/ProFormaGeneralInformation/ParkingSelector/ParkingSelector';
import IconWithTooltip from 'components/IconWithTooltip/IconWithTooltip';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import { parseISO } from 'date-fns';
import { initialBasicInformationValues, QUICK_START_LABEL_CLASS_NAME } from '../constants';
import { BasicInformationValues } from '../types';
import { getTenantDateRange, updateParkingAndSpaces } from '../helpers';

export interface BasicInformationProps {
    values: BasicInformationValues;
    proFormaModelId: number;
    onUpdate: (data: any) => void;
}

const BasicInformation: React.FC<BasicInformationProps> = ({ values, onUpdate, proFormaModelId }) => {
    const [formData, setFormData] = useState(() => values || initialBasicInformationValues);

    const {
        lots, createLot, updateLot, deleteLot, totalGrossBuildableArea, totalFar, totalLandAreaSquareFootage,
    } = useGrossBuildableArea(formData.buildableLots.grossBuildableAreaLots || []);

    const parseDate = (dateValue: any): Date => {
        if (typeof dateValue === 'string') {
            return parseISO(dateValue);
        }
        return new Date(dateValue);
    };

    useEffect(() => {
        if (onUpdate) {
            onUpdate(formData);
        }
    }, [formData, onUpdate]);

    useEffect(() => {
        setFormData((prev: BasicInformationValues) => ({
            ...prev,
            buildableLots: {
                grossBuildableAreaLots: lots,
                totalGrossBuildableArea,
                totalFar,
                totalLandAreaSquareFootage,
            },
        }));
    }, [lots, totalGrossBuildableArea, totalFar, totalLandAreaSquareFootage]);

    const handleInputChange = (field: string, value: any) => {
        setFormData((prev: BasicInformationValues) => {
            if (field === 'parking') {
                const updatedFormData = updateParkingAndSpaces(prev, value);
                return updatedFormData;
            }
            if (field === 'startDate' && value) {
                const startDate = new Date(value);
                const constructionStart = new Date(startDate);
                constructionStart.setFullYear(startDate.getFullYear() + 2);
                return { ...prev, [field]: value, constructionStart };
            }
            if (field === 'holdPeriod') {
                const { proFormaSaleDate } = getTenantDateRange(prev.startDate, prev.constructionStart, prev.constructionDuration, value);
                return { ...prev, [field]: value, saleDate: proFormaSaleDate };
            }
            return { ...prev, [field]: value };
        });
    };

    const formatDate = (date: Date | null) => {
        if (date) {
            return new Date(date.getFullYear(), date.getMonth(), 1);
        }
        return null;
    };

    return (
        <div className="BasicInformation p-3">
            <div className="BasicInformation__form">

                {/* KEY DATES SECTION */}
                <div>
                    <div className="BasicInformation__title">Key Dates</div>
                    <div className="BasicInformation__subtitle">
                        Determine key dates and durations for this pro forma
                    </div>
                    <div className="mt-2 d-flex">
                        <div>
                            <div>
                                <NSLabel className={QUICK_START_LABEL_CLASS_NAME}>Start month</NSLabel>
                                <NSDatePicker
                                    name="startDate"
                                    value={formData.startDate ? parseDate(formData.startDate) : null}
                                    onChange={(date: Date | null) => handleInputChange('startDate', formatDate(date))}
                                    className="BasicInformation__input-width"
                                    showMonthYearPicker
                                    dateFormat="MMMM yyyy"
                                    calendarWidth="400px"
                                />
                            </div>
                            <div className="mt-3">
                                <NSLabel className={QUICK_START_LABEL_CLASS_NAME}>Construction duration</NSLabel>
                                <NSInputNumberStepper
                                    id="constructionDuration"
                                    name="constructionDuration"
                                    value={formData.constructionDuration ? formData.constructionDuration : initialBasicInformationValues.constructionDuration}
                                    onChange={(count: number) => handleInputChange('constructionDuration', count)}
                                    min={1}
                                    step={1}
                                    inputGroupClassName="BasicInformation__input-width"
                                    customSuffix="months"
                                />
                            </div>
                        </div>
                        <div className="ml-3">
                            <div>
                                <NSLabel className={QUICK_START_LABEL_CLASS_NAME}>Construction start</NSLabel>
                                <NSDatePicker
                                    name="constructionStart"
                                    value={formData.constructionStart ? parseDate(formData.constructionStart) : null}
                                    onChange={(date: Date | null) => handleInputChange('constructionStart', formatDate(date))}
                                    className="BasicInformation__input-width"
                                    showMonthYearPicker
                                    dateFormat="MMMM yyyy"
                                    calendarWidth="400px"
                                />
                            </div>
                            <div className="mt-3">
                                <div className="d-flex">
                                    <NSLabel className="pl-0 my-auto font-weight-normal text-dark text-nowrap">Hold period</NSLabel>
                                    <IconWithTooltip
                                        name="holdPeriodTooltip"
                                        icon={faCircleInfo}
                                        tooltipText={HOLD_PERIOD_TOOLTIP_TEXT}
                                        className="my-auto text-dark ml-1"
                                    />
                                </div>
                                <NSInputNumberStepper
                                    id="holdPeriod"
                                    name="holdPeriod"
                                    value={formData.holdPeriod ? formData.holdPeriod : initialBasicInformationValues.holdPeriod}
                                    onChange={(count: number) => handleInputChange('holdPeriod', count)}
                                    min={1}
                                    step={1}
                                    inputGroupClassName="BasicInformation__input-width"
                                    customSuffix="months"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="BasicInformation__separator" />

                {/* SIZE SECTION */}
                <div>
                    <div className="BasicInformation__title">Size</div>
                    <div className="BasicInformation__subtitle">Details about this investment's property size and building</div>

                    <NSLabel className={QUICK_START_LABEL_CLASS_NAME}>Gross buildable area</NSLabel>
                    <CalculateSizeCard
                        lots={lots}
                        onCreateLot={createLot}
                        onDeleteLot={(id: number) => deleteLot(id)}
                        onUpdateLot={updateLot}
                        totalGrossBuildableArea={totalGrossBuildableArea}
                        cardLevel={4}
                    />

                    <RenderIf isTrue={![ProFormaModelIds.APARTMENT, ProFormaModelIds.APARTMENT_WITH_RETAIL].includes(proFormaModelId)}>
                        {/* Buildings - only show for non-apartment models */}
                        <div className="d-flex align-items-center mt-2 w-75">
                            <NSLabel className={QUICK_START_LABEL_CLASS_NAME}>Buildings</NSLabel>
                            <div className="d-flex flex-column">
                                <NSInputNumberStepper
                                    id="buildings"
                                    name="buildings"
                                    value={formData.buildings}
                                    onChange={(count: number) => handleInputChange('buildings', count)}
                                    min={1}
                                    step={1}
                                    inputGroupClassName="BasicInformation__input-width"
                                />
                            </div>
                        </div>

                        {/* Built Gross SF - only show for non-apartment models */}
                        <div className="d-flex align-items-center mt-2 w-75">
                            <NSLabel className={QUICK_START_LABEL_CLASS_NAME}>Built gross SF</NSLabel>
                            <div className="d-flex flex-column w-50">
                                <NSInput
                                    id="builtGrossSquareFootage"
                                    name="builtGrossSquareFootage"
                                    placeholder="Enter built gross SF"
                                    value={formData.builtGrossSquareFootage || ''}
                                    appendInputAddon={<div className="px-2">ft²</div>}
                                    type="number"
                                    onChange={(e: { target: { value: string; }; }) => handleInputChange('builtGrossSquareFootage', e.target.value === '' ? 0 : Number(e.target.value))}
                                    inputGroupClassName="p-0"
                                />
                            </div>
                        </div>
                    </RenderIf>

                </div>
                {/* STORIES SECTION - only show for non-apartment models */}
                <RenderIf isTrue={![ProFormaModelIds.APARTMENT, ProFormaModelIds.APARTMENT_WITH_RETAIL].includes(proFormaModelId)}>
                    <div className="d-flex align-items-center mt-2 w-75">
                        <NSLabel className={QUICK_START_LABEL_CLASS_NAME}>Stories</NSLabel>
                        <div className="d-flex flex-column">
                            <NSInputNumberStepper
                                id="stories"
                                name="stories"
                                value={formData.stories}
                                onChange={(count: number) => handleInputChange('stories', count)}
                                min={1}
                                step={1}
                                inputGroupClassName="BasicInformation__input-width"
                            />
                        </div>
                    </div>
                </RenderIf>

                {/* PARKING SECTION */}
                <div className="d-flex align-items-center mt-2 w-75">
                    <NSLabel className={QUICK_START_LABEL_CLASS_NAME}>Parking</NSLabel>
                    <ParkingSelector
                        options={proFormaModelId === ProFormaModelIds.INDUSTRIAL ? industrialParkingOptions : parkingOptions}
                        value={formData.parking}
                        setSelectedParkingOptions={(options: IOptionAnyValue[]) => handleInputChange('parking', options)}
                    />
                </div>

                {/* PARKING SPACES based on parking selection */}
                {formData.parking?.some((p: any) => p.value === STRUCTURED_PARKING) && (
                    <div className="d-flex my-2 w-75">
                        <NSLabel className={QUICK_START_LABEL_CLASS_NAME}>Structured Parking Spaces</NSLabel>
                        <NSInputNumberStepper
                            id="structuredParkingSpaces"
                            name="structuredParkingSpaces"
                            value={formData.structuredParkingSpaces}
                            onChange={(count: number) => handleInputChange('structuredParkingSpaces', count)}
                            min={0}
                            step={1}
                            inputGroupClassName="BasicInformation__input-width"
                        />
                    </div>
                )}

                {formData.parking?.some((p: any) => p.value === SURFACE_PARKING) && (
                    <div className="d-flex my-2 w-75">
                        <NSLabel className={QUICK_START_LABEL_CLASS_NAME}>Surface Parking Spaces</NSLabel>
                        <NSInputNumberStepper
                            id="surfaceParkingSpaces"
                            name="surfaceParkingSpaces"
                            value={formData.surfaceParkingSpaces}
                            onChange={(count: number) => handleInputChange('surfaceParkingSpaces', count)}
                            min={0}
                            step={1}
                            inputGroupClassName="BasicInformation__input-width"
                        />
                    </div>
                )}

                {formData.parking?.some((p: any) => p.value === UNDERGROUND_PARKING) && (
                    <div className="d-flex my-2 w-75">
                        <NSLabel className={QUICK_START_LABEL_CLASS_NAME}>Underground Parking Spaces</NSLabel>
                        <NSInputNumberStepper
                            id="undergroundParkingSpaces"
                            name="undergroundParkingSpaces"
                            value={formData.undergroundParkingSpaces}
                            onChange={(count: number) => handleInputChange('undergroundParkingSpaces', count)}
                            min={0}
                            step={1}
                            inputGroupClassName="BasicInformation__input-width"
                        />
                    </div>
                )}

                {formData.parking?.some((p: any) => p.value === TRAILER_PARKING) && (
                    <div className="d-flex my-2 w-75">
                        <NSLabel className={QUICK_START_LABEL_CLASS_NAME}>Trailer Parking Spaces</NSLabel>
                        <NSInputNumberStepper
                            id="trailerParkingSpaces"
                            name="trailerParkingSpaces"
                            value={formData.trailerParkingSpaces}
                            onChange={(count: number) => handleInputChange('trailerParkingSpaces', count)}
                            min={0}
                            step={1}
                            inputGroupClassName="BasicInformation__input-width"
                        />
                    </div>
                )}

                {/* Optional fields section for apartment models */}
                <RenderIf isTrue={[ProFormaModelIds.APARTMENT, ProFormaModelIds.APARTMENT_WITH_RETAIL].includes(proFormaModelId)}>
                    <ProFormaSizeOptionalFields
                        values={formData}
                        handleInputChange={handleInputChange}
                        rowClassName="d-flex align-items-center mt-2 w-75"
                        labelClassName={QUICK_START_LABEL_CLASS_NAME}
                        inputClassName="BasicInformation__input-width"
                        useSocketContext={false}
                    />
                </RenderIf>

                {/* ASSUMPTIONS SECTION */}
                <div className="BasicInformation__separator" />
                <div className="BasicInformation__title">Assumptions</div>
                <div className="BasicInformation__subtitle">Assumptions about the sale of this investment</div>
                <div className="w-75">
                    {/* Current Market Cap Rate */}
                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className="BasicInformation__label my-auto text-white font-weight-normal mr-2">
                            Current market cap rate
                        </NSLabel>
                        <div className="d-flex flex-column">
                            <NSInputNumberStepper
                                id="currentMarketCapRate"
                                name="currentMarketCapRate"
                                value={Number(numeralFormatter(formData.currentMarketCapRate, '0.00'))}
                                onChange={(count: number) => handleInputChange('currentMarketCapRate', count)}
                                min={0.1}
                                step={0.1}
                                percentage
                                inputGroupClassName="BasicInformation__input-width"
                            />
                        </div>
                    </div>

                    {/* Projected Cap Rate at Sale */}
                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className="BasicInformation__label my-auto text-white font-weight-normal mr-2">
                            Projected cap rate at sale
                        </NSLabel>
                        <div className="d-flex flex-column">
                            <NSInputNumberStepper
                                id="projectedCapRateAtSale"
                                name="projectedCapRateAtSale"
                                value={Number(numeralFormatter(formData.projectedCapRateAtSale, '0.00'))}
                                onChange={(count: number) => handleInputChange('projectedCapRateAtSale', count)}
                                min={0.1}
                                step={0.1}
                                percentage
                                inputGroupClassName="BasicInformation__input-width"
                            />
                        </div>
                    </div>

                    {/* Selling Costs */}
                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className="BasicInformation__label my-auto text-white font-weight-normal mr-2">
                            Selling costs
                        </NSLabel>
                        <div className="d-flex flex-column">
                            <NSInputNumberStepper
                                id="sellingCosts"
                                name="sellingCosts"
                                value={Number(numeralFormatter(formData.sellingCosts, '0.00'))}
                                onChange={(count: number) => handleInputChange('sellingCosts', count)}
                                min={1}
                                step={1}
                                percentage
                                inputGroupClassName="BasicInformation__input-width"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BasicInformation;
